import React, { useCallback, useEffect, useRef } from 'react';
import { OptionChainExplorer } from '../../../components/trading/OptionChainExplorer';
import type { WidgetInstanceProps } from '../../../components/grid-dashboard';
import { useOrderTicketNavigation } from '../../order-ticket/navigation/OrderTicketNavigationContext';

/* ── Inner component that assumes context is available ─────────────── */

const OptionMonitorInner: React.FC = () => {
  const {
    ticksBySymbol,
    retainSymbolSubscription,
    releaseSymbolSubscription,
  } = useOrderTicketNavigation();

  const retainedSymbolsRef = useRef<Set<string>>(new Set());

  const handleSubscribe = useCallback(
    (symbol: string) => {
      retainedSymbolsRef.current.add(symbol);
      retainSymbolSubscription(symbol);
    },
    [retainSymbolSubscription],
  );

  const handleUnsubscribe = useCallback(
    (symbol: string) => {
      retainedSymbolsRef.current.delete(symbol);
      releaseSymbolSubscription(symbol);
    },
    [releaseSymbolSubscription],
  );

  const handleSelectSymbol = useCallback((_symbol: string) => {
    // No-op — the widget does not pre-fill a global order ticket.
  }, []);

  // Release all retained subscriptions on unmount.
  useEffect(() => {
    const retained = retainedSymbolsRef.current;
    return () => {
      retained.forEach((sym) => releaseSymbolSubscription(sym));
      retained.clear();
    };
  }, [releaseSymbolSubscription]);

  return (
    <OptionChainExplorer
      ticksBySymbol={ticksBySymbol}
      onSubscribe={handleSubscribe}
      onUnsubscribe={handleUnsubscribe}
      onSelectSymbol={handleSelectSymbol}
    />
  );
};

/* ── Error boundary to catch missing context ───────────────────────── */

interface ErrorBoundaryState {
  hasError: boolean;
  message: string;
}

class OptionMonitorErrorBoundary extends React.Component<
  { children: React.ReactNode },
  ErrorBoundaryState
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, message: '' };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return {
      hasError: true,
      message: error.message || 'Unexpected error in Option Monitor widget.',
    };
  }

  componentDidCatch(error: Error) {
    // eslint-disable-next-line no-console
    console.error('OptionMonitorWidget render error:', error);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: '1rem', color: 'var(--color-error, #e74c3c)' }}>
          <strong>Option Monitor unavailable</strong>
          <p style={{ marginTop: '0.5rem' }}>
            {this.state.message.includes('OrderTicketNavigationProvider')
              ? 'OrderTicketNavigationContext is not available. Ensure this widget is rendered inside an OrderTicketNavigationProvider.'
              : this.state.message || 'Unable to render Option Monitor widget.'}
          </p>
        </div>
      );
    }
    return this.props.children;
  }
}

/**
 * Self-contained Option Monitor widget.
 *
 * Obtains live tick data and symbol subscription helpers from
 * OrderTicketNavigationContext, then delegates all UI and contract-lookup
 * logic to the existing OptionChainExplorer component.
 *
 * Tracks every symbol subscription it retains and releases them all on
 * unmount so the ref-counted subscription system stays balanced.
 */
export const OptionMonitorWidget: React.FC<WidgetInstanceProps> = (_props) => {
  return (
    <OptionMonitorErrorBoundary>
      <OptionMonitorInner />
    </OptionMonitorErrorBoundary>
  );
};

export default OptionMonitorWidget;
